import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { exchangeLaunch, loginTeacher } from "../api/auth";
import { getTeacherSubscription } from "../api/teacher";
import {
  resolveTutorcloudBillingUrl,
  TUTORCLOUD_HANDOFF_PREFIX,
} from "../utils/tutorcloudTeacherLinks";
import { clearTeacherCache, primeTeacherCache } from "../hooks/useTeacher";
import {
  ApiError,
  clearStoredAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
} from "../api/client";
import {
  clearLaunchParamsFromUrl,
  readLaunchParams,
} from "../utils/tutorcloudLaunch";
import { normalizeTeacherAccessibility } from "../utils/teacherAccessibility";
import {
  canOpenTutorCloud,
  canUseTemaTools,
  EMPTY_SUBSCRIPTION,
  normalizeSubscription,
} from "../utils/subscriptionAccess";

const launchExchangeCache = new Map();

function launchCacheKey(params) {
  if (params?.code) return `code:${params.code}`;
  if (params?.token) return `token:${params.token}`;
  return "";
}

function exchangeLaunchOnce(params) {
  const key = launchCacheKey(params);
  if (!key) {
    return Promise.reject(new Error("Missing launch code or token"));
  }
  if (!launchExchangeCache.has(key)) {
    launchExchangeCache.set(
      key,
      exchangeLaunch({
        code: params.code,
        launchToken: params.token,
      }),
    );
  }
  return launchExchangeCache.get(key);
}

const STORAGE_KEY = "tema_teacher_id";
const SUBSCRIPTION_KEY = "tema_subscription";
const ACCESSIBILITY_KEY = "tema_accessibility";
const PINNED_TOOLS_STORAGE_KEY = "tema.pinned-tool-ids";
const TEACHER_SESSION_KEYS = [
  STORAGE_KEY,
  "tema_access_token",
  SUBSCRIPTION_KEY,
  ACCESSIBILITY_KEY,
  "tema_onboarding_profile",
  "tema_generation_job_ids",
  "tema_dismissed_completion_jobs",
  "tema_pending_job_view",
];
const TEACHER_SESSION_PREFIXES = [
  "tema-workspace:",
  "tema:familiar-topics:v2:",
  TUTORCLOUD_HANDOFF_PREFIX,
];

function hasLaunchCredential(params) {
  return Boolean(params?.code || params?.token);
}

export function getStoredTeacherId() {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY)?.trim();
    return value || undefined;
  } catch {
    return undefined;
  }
}

function readStoredSubscription() {
  try {
    const raw = sessionStorage.getItem(SUBSCRIPTION_KEY);
    if (!raw) {
      return { ...EMPTY_SUBSCRIPTION };
    }
    return normalizeSubscription(JSON.parse(raw));
  } catch {
    return { ...EMPTY_SUBSCRIPTION };
  }
}

function persistTeacherId(teacherId) {
  sessionStorage.setItem(STORAGE_KEY, teacherId);
}

function persistSubscription(subscription) {
  sessionStorage.setItem(
    SUBSCRIPTION_KEY,
    JSON.stringify(normalizeSubscription(subscription)),
  );
}

function readStoredAccessibility() {
  try {
    const raw = sessionStorage.getItem(ACCESSIBILITY_KEY);
    if (!raw) return undefined;
    return normalizeTeacherAccessibility(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

function persistAccessibility(accessibility) {
  if (!accessibility) {
    sessionStorage.removeItem(ACCESSIBILITY_KEY);
    return;
  }
  sessionStorage.setItem(ACCESSIBILITY_KEY, JSON.stringify(accessibility));
}

function clearTeacherScopedSession() {
  for (const key of TEACHER_SESSION_KEYS) {
    sessionStorage.removeItem(key);
  }
  clearStoredAccessToken();
  try {
    const prefixedKeys = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key && TEACHER_SESSION_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        prefixedKeys.push(key);
      }
    }
    for (const key of prefixedKeys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage failures in private browsing.
  }
}

function clearPinnedToolsIfTeacherChanged(previousTeacherId, nextTeacherId) {
  if (!previousTeacherId || previousTeacherId === nextTeacherId) {
    return;
  }
  try {
    localStorage.removeItem(PINNED_TOOLS_STORAGE_KEY);
  } catch {
    // Ignore storage failures in private browsing.
  }
}

let activeLaunchHandoff = null;

function beginLaunchHandoff() {
  const params = readLaunchParams();
  if (!hasLaunchCredential(params)) {
    return null;
  }
  const key = launchCacheKey(params);
  if (activeLaunchHandoff?.key === key) {
    return activeLaunchHandoff;
  }
  const previousTeacherId = getStoredTeacherId();
  clearTeacherScopedSession();
  clearTeacherCache();
  launchExchangeCache.clear();
  activeLaunchHandoff = {
    key,
    params,
    previousTeacherId,
  };
  return activeLaunchHandoff;
}

function finishLaunchHandoff(key) {
  if (activeLaunchHandoff?.key === key) {
    activeLaunchHandoff = null;
  }
}

const AuthContext = createContext(null);

function isSessionAuthenticated() {
  return Boolean(getStoredTeacherId() && getStoredAccessToken());
}

export function AuthProvider({ children }) {
  const [launchHandoff] = useState(() => beginLaunchHandoff());
  const [teacherId, setTeacherId] = useState(() => {
    if (launchHandoff) {
      return undefined;
    }
    return isSessionAuthenticated() ? getStoredTeacherId() : undefined;
  });
  const [subscription, setSubscription] = useState(() =>
    launchHandoff ? { ...EMPTY_SUBSCRIPTION } : readStoredSubscription(),
  );
  const [accessibility, setAccessibility] = useState(() =>
    launchHandoff ? undefined : readStoredAccessibility(),
  );
  const [loginError, setLoginError] = useState(null);
  const [launchError, setLaunchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLaunching, setIsLaunching] = useState(() => Boolean(launchHandoff));

  useEffect(() => {
    if (!launchHandoff) {
      setIsLaunching(false);
      return undefined;
    }

    const { params: launchParams, previousTeacherId, key } = launchHandoff;
    let cancelled = false;

    async function bootstrapFromLaunch() {
      setIsLaunching(true);
      setLaunchError(null);
      try {
        const result = await exchangeLaunchOnce(launchParams);
        if (cancelled) {
          return;
        }
        // The launch credential is the signed-in account. The previous
        // session was discarded before the first render.
        clearTeacherCache();
        setStoredAccessToken(result.accessToken);
        persistTeacherId(result.teacherId);
        const nextSubscription = normalizeSubscription(result.subscription);
        persistSubscription(nextSubscription);
        const nextAccessibility = normalizeTeacherAccessibility(result.accessibility);
        persistAccessibility(nextAccessibility);
        clearPinnedToolsIfTeacherChanged(previousTeacherId, result.teacherId);
        setTeacherId(result.teacherId);
        setSubscription(nextSubscription);
        setAccessibility(nextAccessibility);
        primeTeacherCache(result.teacherId, result.teacher);
        setLaunchError(null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        clearTeacherScopedSession();
        clearTeacherCache();
        setTeacherId(undefined);
        setSubscription({ ...EMPTY_SUBSCRIPTION });
        setAccessibility(undefined);
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Could not sign in from TutorCloud. Please try again.";
        setLaunchError(message);
      } finally {
        if (!cancelled) {
          clearLaunchParamsFromUrl();
          finishLaunchHandoff(key);
          setIsLaunching(false);
        }
      }
    }

    bootstrapFromLaunch();

    return () => {
      cancelled = true;
    };
  }, [launchHandoff]);

  useEffect(() => {
    if (isLaunching || !teacherId || !getStoredAccessToken()) {
      return undefined;
    }

    let cancelled = false;

    getTeacherSubscription()
      .then((sub) => {
        if (cancelled) {
          return;
        }
        const next = normalizeSubscription(sub);
        persistSubscription(next);
        setSubscription(next);
      })
      .catch(() => {
        // Keep session subscription from login/launch if refresh fails.
      });

    return () => {
      cancelled = true;
    };
  }, [teacherId, isLaunching]);

  const establishSession = useCallback((result) => {
    setStoredAccessToken(result.accessToken);
    persistTeacherId(result.teacherId);
    const nextSubscription = normalizeSubscription(result.subscription);
    persistSubscription(nextSubscription);
    const nextAccessibility = normalizeTeacherAccessibility(result.accessibility);
    persistAccessibility(nextAccessibility);
    setTeacherId(result.teacherId);
    setSubscription(nextSubscription);
    setAccessibility(nextAccessibility);
    if (result.teacher) {
      primeTeacherCache(result.teacherId, result.teacher);
    }
    return result;
  }, []);

  const login = useCallback(async (email, password) => {
    setLoginError(null);
    setLaunchError(null);
    setIsSubmitting(true);
    try {
      const result = await loginTeacher(email, password);
      return establishSession(result);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Sign in failed. Please try again.";
      setLoginError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [establishSession]);

  const logout = useCallback(() => {
    clearLaunchParamsFromUrl();
    clearTeacherScopedSession();
    clearTeacherCache();
    launchExchangeCache.clear();
    activeLaunchHandoff = null;
    setTeacherId(undefined);
    setSubscription({ ...EMPTY_SUBSCRIPTION });
    setAccessibility(undefined);
    setLoginError(null);
    setLaunchError(null);
  }, []);

  const canOpenTc = useMemo(
    () => canOpenTutorCloud(subscription),
    [subscription],
  );

  const canUseTools = useMemo(
    () => canUseTemaTools(subscription),
    [subscription],
  );

  const tcBillingUrl = useMemo(
    () => resolveTutorcloudBillingUrl(normalizeSubscription(subscription).tcBillingUrl),
    [subscription],
  );

  const value = useMemo(
    () => ({
      teacherId,
      isAuthenticated: Boolean(teacherId && getStoredAccessToken()),
      login,
      establishSession,
      logout,
      loginError,
      launchError,
      isSubmitting,
      isLaunching,
      subscription,
      canOpenTutorCloud: canOpenTc,
      canUseTemaTools: canUseTools,
      tcBillingUrl,
      accessibility,
      clearLoginError: () => setLoginError(null),
      clearLaunchError: () => setLaunchError(null),
    }),
    [
      teacherId,
      login,
      establishSession,
      logout,
      loginError,
      launchError,
      isSubmitting,
      isLaunching,
      subscription,
      canOpenTc,
      canUseTools,
      tcBillingUrl,
      accessibility,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
